import { AdditionalHeaderRow, CustomColumnDef, TableOptions } from '@/types/table';
import { Logger } from '@/utils/Logger';
import { capSortColumns } from '@/utils/tableUtils';
import { GroupingState, SortingState } from '@tanstack/react-table';
import { useEffect } from 'react';
import styles from '../Table.module.scss';
import { DEFAULT_PAGE_SIZE } from './constants';

const ModuleName = 'Table';

const hasHeader = (header: unknown): boolean => typeof header !== 'string' || header !== '';

export const normalizeMergedGroups = <T,>(columns: CustomColumnDef<T>[]): CustomColumnDef<T>[] => {
  const result = [...columns];

  for (let i = 0; i < result.length; i++) {
    if (result[i].mergedGroup) continue;

    const header = result[i].header;
    const label = typeof header === 'string' ? header : '';
    if (!label) continue;

    const childStartIndex = i + 1;
    let childEndIndex = childStartIndex;

    while (childEndIndex < result.length) {
      const column = result[childEndIndex];
      if (column.mergedGroup || hasHeader(column.header)) break;
      if (column.visible === false) {
        childEndIndex++;
        continue;
      }
      childEndIndex++;
    }

    const hasVisibleChildren = result.slice(childStartIndex, childEndIndex).some(col => col.visible !== false);

    if (hasVisibleChildren) {
      for (let j = i; j < childEndIndex; j++) {
        result[j] = { ...result[j], mergedGroup: label };
      }
    }
  }

  return result;
};

export const getAdditionalHeaderLabels = <T,>(
  additionalHeaderRows?: AdditionalHeaderRow<T>[],
): Record<string, string> | undefined => {
  const firstRow = additionalHeaderRows?.[0];
  if (!firstRow?.data) return undefined;
  const labels: Record<string, string> = {};
  for (const [key, value] of Object.entries(firstRow.data)) {
    if (typeof value === 'string') {
      labels[key] = value;
    } else if (value && typeof value === 'object' && 'label' in value && typeof value.label === 'string') {
      labels[key] = value.label;
    }
  }
  return Object.keys(labels).length > 0 ? labels : undefined;
};

export const sortColumnsByOrder = <T,>(columns: CustomColumnDef<T>[], columnOrder: string[]): CustomColumnDef<T>[] => {
  if (!columnOrder.length) return columns;

  const orderIndexMap = new Map(columnOrder.map((id, index) => [id, index]));
  return [...columns].sort((columnA, columnB) => {
    const orderIndexA = orderIndexMap.get(String(columnA.id)) ?? Infinity;
    const orderIndexB = orderIndexMap.get(String(columnB.id)) ?? Infinity;
    return orderIndexA - orderIndexB;
  });
};

const HIGHLIGHT_MAX_RETRY_ATTEMPTS = 15;
const HIGHLIGHT_INITIAL_DELAY_MS = 100;
const HIGHLIGHT_MAX_DELAY_MS = 500;

export const getPageSize = <T,>({ options: { pageSize } = {} }: { options?: TableOptions<T> }): number =>
  pageSize ?? DEFAULT_PAGE_SIZE;

export const getNextSortingState = (
  sorting: SortingState | undefined,
  columnId: string,
  sortDescFirst = false,
): SortingState => {
  const currentSortingState = sorting ?? [];
  const currentSorting = currentSortingState.find(sort => sort.id === columnId);
  const remainingSorting = currentSortingState.filter(sort => sort.id !== columnId);

  if (!currentSorting) {
    return capSortColumns([{ id: columnId, desc: sortDescFirst }, ...remainingSorting]);
  }

  if (currentSorting.desc === sortDescFirst) {
    return capSortColumns([{ id: columnId, desc: !sortDescFirst }, ...remainingSorting]);
  }

  return remainingSorting;
};

export const getSortPriority = (sorting: SortingState | undefined, columnId: string): number | undefined => {
  const currentSortingState = sorting ?? [];
  const sortIndex = currentSortingState.findIndex(sort => sort.id === columnId);

  if (sortIndex === -1) {
    return undefined;
  }

  return sortIndex + 1;
};

type ConvertToCamelCase = (headerName: string) => string;

export const convertToCamelCase: ConvertToCamelCase = headerName => {
  return headerName
    .replace(/\s+/g, '-')
    .replace(/\/+/g, '-')
    .toLowerCase()
    .replace(/-(.)/g, (_, match) => match.toUpperCase());
};

const hasProperty = (value: object, key: string): value is Record<string, unknown> => key in value;

export const getNumberOfRowsByGrouping = <T,>(data: T[], grouping: GroupingState): number => {
  if (!data.length) return 0;

  const [groupingKey] = grouping;
  if (!groupingKey) return data.length;

  return new Set(
    data.map(dataItem => {
      if (typeof dataItem !== 'object' || dataItem === null || !hasProperty(dataItem, groupingKey)) return undefined;
      return dataItem[groupingKey];
    }),
  ).size;
};

export function useValidateTableProps<T>(
  tableId: string,
  columns: CustomColumnDef<T>[],
  tableOptions: TableOptions<T> = {},
): void {
  useEffect(() => {
    // Validate compatibility of "GroupPrefetched" flow
    if (tableOptions.groupingFlow === 'GroupPrefetched' && !!tableOptions.lazy) {
      Logger.warn(
        ModuleName,
        `simultaneous usage of "lazy" and "groupingFlow=GroupPrefetched" detected at "table#${tableId}". These props are incompatible. Pls, check your code!`,
      );
    }
  }, [tableOptions, tableId, columns]);
}

/**
 * Generates a CSS attribute selector that safely escapes special characters.
 * Escapes field and value strings to prevent CSS injection and handle special characters like ^, :, /.
 * @param field - The data attribute field name to match
 * @param value - The data attribute value to match
 * @returns A CSS selector string in format [data-highlight-field="..."][data-highlight-value="..."]
 */
export const getSafeCssSelector = (field: string, value: string): string => {
  const safeField = CSS.escape(field).replace(/:/g, '\\:');

  const safeValue = CSS.escape(String(value)).replace(/:/g, '\\:').replace(/\//g, '\\/');

  return `[data-highlight-field="${safeField}"][data-highlight-value="${safeValue}"]`;
};

/**
 * Highlights a table row by finding it via data attributes and applying a flash animation.
 * Implements retry logic with exponential backoff to handle DOM rendering delays.
 * Automatically scrolls the highlighted row into view if not visible.
 * @param field - The data attribute field name used to identify the row
 * @param value - The data attribute value used to identify the row
 * @param highlightClass - CSS class to apply for the flash animation (defaults to styles.highlightFlash)
 * @param onAnimationEnd - Optional callback invoked when the animation completes
 * @returns Cleanup function to cancel pending operations
 */
export const highlightRow = (
  field: string,
  value: string,
  highlightClass: string = styles.highlightFlash,
  onAnimationEnd?: () => void,
): (() => void) => {
  const selector = getSafeCssSelector(field, value);
  let animationFrame: number;
  let timeoutId: NodeJS.Timeout;
  let attemptCount = 0;

  const cleanup = () => {
    cancelAnimationFrame(animationFrame);
    clearTimeout(timeoutId);
  };

  const attemptHighlight = () => {
    attemptCount++;
    const rowNode = document.querySelector(selector);
    const rowElement = rowNode instanceof HTMLElement ? rowNode : null;

    if (rowElement) {
      const scrollParent = rowElement.closest(
        '.ReactVirtualized__Grid, .scrollable, [style*="overflow: auto"], [style*="overflow-y: auto"]',
      );

      if (scrollParent) {
        const { top, bottom } = rowElement.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();

        const isVisible = top >= parentRect.top && bottom <= parentRect.bottom;

        if (!isVisible) {
          rowElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      } else {
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      rowElement.classList.add(highlightClass);
      const cells = rowElement.querySelectorAll('td');
      cells.forEach(cell => cell.classList.add(highlightClass));

      const handleAnimationEnd = () => {
        rowElement.classList.remove(highlightClass);
        cells.forEach(cell => cell.classList.remove(highlightClass));
        rowElement.removeEventListener('animationend', handleAnimationEnd);
        onAnimationEnd?.();
        cleanup();
      };

      rowElement.addEventListener('animationend', handleAnimationEnd);
      return true;
    }

    if (attemptCount < HIGHLIGHT_MAX_RETRY_ATTEMPTS) {
      const delay = Math.min(HIGHLIGHT_INITIAL_DELAY_MS * Math.pow(2, attemptCount), HIGHLIGHT_MAX_DELAY_MS);
      timeoutId = setTimeout(() => {
        animationFrame = requestAnimationFrame(attemptHighlight);
      }, delay);
    } else {
      cleanup();
    }

    return false;
  };

  animationFrame = requestAnimationFrame(attemptHighlight);

  return cleanup;
};

export const getRowId = (row: unknown): string | undefined => {
  if (typeof row !== 'object' || row === null || !('id' in row)) {
    return undefined;
  }

  return typeof row.id === 'string' ? row.id : undefined;
};
